import React from "react";
import styles from "../css/components/EligibilityButton.module.css";
import modal from "../css/components/ClaimResultModal.module.css";

export default function ClaimResultModal({ submitting, result, error, reversal, onContinue, onDiscard, discarding }) {
  const money = (v) => (v == null ? null : `R ${Number(v).toFixed(2)}`);

  const overallMessages = (result?.messages || []).filter((m) => m.level !== "item");

  return (
    <div className={modal.overlay}>
      <div className={modal.card}>
        <h3 className={modal.title}>Medical Aid Claim</h3>

        {/* Amend flow: show that the original claim was reversed first.
            hnet = HealthNet's own switch reference; fund = the scheme's own
            authorization number (only set on switch-out) — these are distinct
            per the MediKredit XML spec and must not be shown as the same value. */}
        {reversal && reversal.reversed && (
          <div className={`${styles.result} ${styles.approved} ${modal.reversalLine}`}>
            <div className={styles.resultBody}>
              <span className={styles.resultTitle}>Original claim reversed ✓</span>
              {reversal.reversedClaimAuth && (
                <span className={modal.hint}>Reversed claim switch ref · {reversal.reversedClaimAuth}</span>
              )}
              {reversal.reversedClaimFundAuth && (
                <span className={modal.hint}>Reversed claim auth # · {reversal.reversedClaimFundAuth}</span>
              )}
              {reversal.authNumber && (
                <span className={styles.authChip}>Switch ref · {reversal.authNumber}</span>
              )}
            </div>
          </div>
        )}

        {submitting && (
          <div className={`${styles.result} ${modal.submitting}`}>
            <span className={`${styles.spinner} ${modal.submitSpinner}`} />
            <div className={styles.resultBody}>
              <span className={styles.resultTitle}>Submitting claim to the scheme…</span>
            </div>
          </div>
        )}

        {/* Claim header: transaction number and both switch/auth numbers, shown
            regardless of approved/rejected — accreditation requires the Hnet
            (switch reference) to be visible on both outcomes, not just approved. */}
        {!submitting && result && (
          <div className={modal.claimHeader}>
            {result.transactionNumber && (
              <span className={modal.headerItem} title="Our own generated reference number for this submission — separate from MediKredit's switch reference below">
                Transaction # {result.transactionNumber}
              </span>
            )}
            {result.memberNumber && (
              <span className={modal.headerItem}>Member # {result.memberNumber}</span>
            )}
            {result.authNumber && (
              <span className={styles.authChip}>
                {result.switchHouse === "allegra" ? "Admin ref" : "Switch ref (Hnet)"} · {result.authNumber}
              </span>
            )}
            {result.fundAuthNumber && (
              <span className={styles.authChip}>Auth # (Fund) · {result.fundAuthNumber}</span>
            )}
          </div>
        )}

        {!submitting && result && (
          <div
            className={`${styles.result} ${
              result.approved ? styles.approved : styles.rejected
            }`}
          >
            <div className={styles.resultBody}>
              <span className={styles.resultTitle}>
                {result.resultText === "warning"
                  ? "Accepted with warnings"
                  : result.approved
                  ? "Claim accepted ✓"
                  : "Claim rejected"}
              </span>

              {/* Overall financials — submitted vs approved gross, nett due from
                  the scheme, dispensing/professional fee, and overcharge if any. */}
              {result.totals && (
                <div className={modal.financials}>
                  {result.totals.subGross != null && (
                    <span>Submitted gross {money(result.totals.subGross)}</span>
                  )}
                  {result.totals.gross != null && (
                    <span>Approved gross {money(result.totals.gross)}</span>
                  )}
                  {result.totals.nett != null && <span>Nett due {money(result.totals.nett)}</span>}
                  {result.totals.gross != null && result.totals.nett != null && (
                    <span>
                      Co-payment {money(Number(result.totals.gross) - Number(result.totals.nett))}
                    </span>
                  )}
                  {result.totals.fees?.dispensing != null && (
                    <span>Dispensing fee {money(result.totals.fees.dispensing)}</span>
                  )}
                  {result.totals.fees?.professional != null && (
                    <span>Professional fee {money(result.totals.fees.professional)}</span>
                  )}
                </div>
              )}

              {/* Overall (transaction-level) messages only — per-item messages are
                  shown against their own line in the item table below. */}
              {overallMessages.length > 0 && (
                <ul className={styles.reasons}>
                  {overallMessages.map((m, i) => (
                    <li key={i}>
                      {m.desc} <span className={modal.code}>({m.code})</span>
                    </li>
                  ))}
                </ul>
              )}

              {!result.approved && result.resultText !== "warning" && (
                <span className={modal.hint}>
                  This script was not saved — please fix the issue and start again.
                </span>
              )}
              {result.resultText === "warning" && (
                <span className={modal.hint}>
                  You can retry the claim later from the Script Details page.
                </span>
              )}
            </div>
          </div>
        )}

        {/* The submitted price exceeded MediKredit's regulated/adjudicated price —
            the patient may have already been charged based on the wrong number, so
            this needs to be impossible to miss, not buried in a generic warning list. */}
        {!submitting && result && result.totals?.overCharge > 0 && (
          <div className={`${styles.result} ${styles.rejected}`}>
            <div className={styles.resultBody}>
              <span className={styles.resultTitle}>⚠ Price mismatch — you may have overcharged</span>
              <span>
                Submitted gross was {money(result.totals.subGross)}, but MediKredit only approved{" "}
                {money(result.totals.gross)} — a difference of {money(result.totals.overCharge)}.
              </span>
              <span className={modal.hint}>
                Check what the patient was charged against this approved amount before finalising.
              </span>
              {onDiscard && (
                <>
                  <button
                    type="button"
                    className={modal.discardBtn}
                    onClick={onDiscard}
                    disabled={submitting || discarding}
                  >
                    {discarding ? "Discarding…" : "Discard — don't save this claim"}
                  </button>
                  <span className={modal.hint}>
                    MediKredit has already accepted this claim — discarding only removes it from
                    our own records; it does not reverse it at the scheme.
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Per-item status: NAPPI code, what we submitted vs what MediKredit
            approved, item status, and that item's own warnings/rejections. */}
        {!submitting && result && Array.isArray(result.items) && result.items.length > 0 && (
          <table className={modal.itemTable}>
            <thead>
              <tr>
                <th>Line</th>
                <th>NAPPI</th>
                <th>Submitted price</th>
                <th>Approved price</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {result.items.map((it, i) => {
                const price = it.nappi?.price;
                const profFee = it.nappi?.fees?.professional;
                const showFee = profFee != null;
                return (
                  <tr key={i}>
                    <td>{it.number ?? i + 1}</td>
                    <td>{it.nappi?.code || it.submittedNappi || "—"}</td>
                    <td>{money(it.submittedPrice) ?? "—"}</td>
                    <td>
                      {money(price) ?? "—"}
                      {showFee && (
                        <div className={modal.itemMessage}>
                          + {money(profFee)} professional fee
                        </div>
                      )}
                    </td>
                    <td>
                    <span
                      className={
                        it.status === "R"
                          ? modal.itemRejected
                          : it.status === "W"
                          ? modal.itemWarning
                          : modal.itemApproved
                      }
                    >
                      {it.statusText || it.status || "—"}
                    </span>
                    {[...(it.warnings || []), ...(it.rejections || [])].map((m, mi) => (
                      <div key={mi} className={modal.itemMessage}>
                        {m.description} ({m.code})
                      </div>
                    ))}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {!submitting && error && (
          <div className={`${styles.result} ${styles.errorBox}`}>
            <div className={styles.resultBody}>
              <span className={styles.resultTitle}>Couldn't submit claim</span>
              <span>{error}</span>
              <span className={modal.hint}>
                The script is saved — you can retry the claim from Script Details.
              </span>
            </div>
          </div>
        )}

        <button type="button" className={modal.continueBtn} onClick={onContinue} disabled={submitting}>
          {submitting
            ? "Please wait…"
            : result && !result.approved && result.resultText !== "warning"
            ? "Back to Script"
            : "Continue"}
        </button>
      </div>
    </div>
  );
}
